"use client";

import { CSSProperties, JSX, RefObject, useMemo, useRef } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  Legend,
  LegendIndex,
  LegendItem,
  LegendLabel,
  LegendValue,
} from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import type { ModulePyramidData, PyramidVisualRef } from "@/types/modules";
import { VisualRefRender, resolveLndevSlug } from "@/components/handyout-components/visual-ref";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import { accentChartColors } from "./module-accent";

const tabular: CSSProperties = { fontVariantNumeric: "tabular-nums" };

interface PyramidTier {
  id: string;
  label: string;
  value: string;
  color?: string;
  variant?: "filled" | "dashed";
  visual?: PyramidVisualRef;
}

function resolveVisual(tier: PyramidTier, index: number): PyramidVisualRef {
  return tier.visual ?? { type: "number", value: index + 1 };
}

function resolveTierColor(
  tier: PyramidTier,
  index: number,
  palette: string[],
): string {
  return tier.color ?? palette[index % palette.length];
}

interface PyramidGeometry {
  cx: number;
  top: number;
  height: number;
  maxWidth: number;
  tierHeight: number;
}

interface TierGeomResult {
  yTop: number;
  yBottom: number;
  topWidth: number;
  bottomWidth: number;
  centerY: number;
  centerWidth: number;
}

function tierGeom(
  i: number,
  N: number,
  py: PyramidGeometry,
  inverted: boolean,
): TierGeomResult {
  const yTop = py.top + i * py.tierHeight + 1;
  const yBottom = py.top + (i + 1) * py.tierHeight - 1;
  const topFrac = inverted ? (N - i) / N : i / N;
  const bottomFrac = inverted ? (N - i - 1) / N : (i + 1) / N;
  const topWidth = py.maxWidth * topFrac;
  const bottomWidth = py.maxWidth * bottomFrac;
  const centerY = (yTop + yBottom) / 2;
  const centerWidth = (topWidth + bottomWidth) / 2;
  return { yTop, yBottom, topWidth, bottomWidth, centerY, centerWidth };
}

function TierTooltip({
  tiers,
  active,
  palette,
  anchorRefs,
}: {
  tiers: PyramidTier[];
  active: number | null;
  palette: string[];
  anchorRefs: RefObject<Record<string, SVGGElement | null>>;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const tier = active !== null ? tiers[active] : null;
  const color = tier ? resolveTierColor(tier, active ?? 0, palette) : "";
  // NodeTooltip keeps `rows` in a layout-effect dependency array, so a fresh
  // array on every render re-runs its measure and re-creates its observer.
  const rows = useMemo(
    () =>
      tier && active !== null
        ? [
            { color, label: t("Value"), value: tier.value },
            {
              color,
              label: t("Tier"),
              value: t("{index} of {total}", { index: active + 1, total: tiers.length }),
            },
          ]
        : [],
    [tier, active, color, tiers.length, t],
  );

  return (
    <NodeTooltip
      visible={tier !== null}
      anchor={tier ? (anchorRefs.current[tier.id] ?? null) : null}
      contentKey={tier?.id}
      title={tier ? stripInlineMarkdown(tier.label) : undefined}
      rows={rows}
    />
  );
}

function tiersToLegendItems(tiers: PyramidTier[], palette: string[]) {
  return tiers.map((t, i) => ({
    id: t.id,
    label: stripInlineMarkdown(t.label),
    value: t.value,
    color: resolveTierColor(t, i, palette),
  }));
}

/**
 * Stable ref setters. An inline `ref={(el) => …}` is a new function on every
 * render, so React detaches and re-attaches every tier's ref each time a hover
 * or a selection changes.
 */
function useTierAnchors(tiers: PyramidTier[]) {
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const setAnchorRef = useMemo(() => {
    const setters: Record<string, (el: SVGGElement | null) => void> = {};
    tiers.forEach((t) => {
      setters[t.id] = (el) => {
        anchorRefs.current[t.id] = el;
      };
    });
    return setters;
  }, [tiers]);
  return { anchorRefs, setAnchorRef };
}

function NodeGlyph({
  visual,
  cx,
  cy,
  fill,
  radius,
  ring,
}: {
  visual: PyramidVisualRef;
  cx: number;
  cy: number;
  fill: string;
  radius: number;
  ring?: string;
}): JSX.Element {
  if (visual.type === "number") {
    return (
      <>
        {ring && (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={ring}
            strokeWidth="1.25"
          />
        )}
        <text
          x={cx}
          y={cy + 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight={500}
          fill={fill}
          style={tabular}
        >
          {visual.value}
        </text>
      </>
    );
  }
  const size = radius * 1.4;
  const half = size / 2;
  if (visual.type === "icon") {
    return (
      <foreignObject x={cx - half} y={cy - half} width={size} height={size}>
        <div className="flex h-full w-full items-center justify-center" style={{ color: fill }}>
          <VisualRefRender visual={visual} className="h-full w-full" />
        </div>
      </foreignObject>
    );
  }
  return (
    <image
      href={resolveLndevSlug(visual.slug)}
      x={cx - half}
      y={cy - half}
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid meet"
    />
  );
}

function NodePyramidChart({
  tiers,
  inverted,
  grayscale,
  accent,
}: {
  tiers: PyramidTier[];
  inverted: boolean;
  grayscale?: boolean;
  accent?: number;
}): JSX.Element {
  const { hovered, selected, effective, setHovered, select, setLabelRef } =
    useLegendHoverSelect();
  const hoverGesture = useHoverGesture();
  const palette = accentChartColors(grayscale, accent);

  const N = tiers.length;
  const svg = { w: 320, h: 320 };
  const maxWidth = 280;
  const height = maxWidth * (Math.sqrt(3) / 2);
  const top = (svg.h - height) / 2;
  const py = { cx: 160, top, height, maxWidth, tierHeight: height / N };
  const nodeR = 15;
  const { svgRef, height: dynamicH } = useSvgContentHeight(svg.h, [tiers]);
  const legendItems = useMemo(
    () => tiersToLegendItems(tiers, palette),
    [tiers, palette],
  );
  const { anchorRefs, setAnchorRef } = useTierAnchors(tiers);

  const apex = { x: py.cx, y: py.top };
  const baseL = { x: py.cx - py.maxWidth / 2, y: py.top + py.height };
  const baseR = { x: py.cx + py.maxWidth / 2, y: py.top + py.height };
  const outlinePath = inverted
    ? `M ${baseL.x} ${apex.y} L ${baseR.x} ${apex.y} L ${apex.x} ${baseL.y} Z`
    : `M ${apex.x} ${apex.y} L ${baseR.x} ${baseL.y} L ${baseL.x} ${baseL.y} Z`;

  return (
    <>
      <div className="shrink-0">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${svg.w} ${dynamicH}`}
          width={svg.w}
          height={dynamicH}
          className="max-w-full h-auto"
          style={{ overflow: "visible" }}
        >
          <path
            d={outlinePath}
            fill="none"
            stroke="var(--grid-strong)"
            strokeWidth="1"
            strokeDasharray="3 4"
            strokeLinejoin="round"
          />

          {tiers.slice(0, -1).map((_, i) => {
            const y = py.top + (i + 1) * py.tierHeight;
            const frac = inverted ? (N - i - 1) / N : (i + 1) / N;
            const w = py.maxWidth * frac;
            return (
              <line
                key={`bound-${i}`}
                x1={py.cx - w / 2}
                y1={y}
                x2={py.cx + w / 2}
                y2={y}
                stroke="var(--grid-strong)"
                strokeWidth="1"
                strokeDasharray="2 4"
                opacity="0.5"
              />
            );
          })}

          {tiers.map((tier, i) => {
            const g = tierGeom(i, N, py, inverted);
            const isH = effective === i;
            const isD = effective !== null && !isH;
            const tierColor = resolveTierColor(tier, i, palette);
            const visual = resolveVisual(tier, i);

            const nodeX = py.cx;
            const nodeY = g.centerY;

            return (
              <g
                key={tier.id}
                ref={setAnchorRef[tier.id]}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) setHovered(i);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) setHovered(null);
                }}
                onClick={() => select(i, { kind: "segment", id: tier.id })}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={isD ? 0.35 : 1}
              >
                {isH && (
                  <circle
                    cx={nodeX}
                    cy={nodeY}
                    r={nodeR + 5}
                    fill="none"
                    stroke={tierColor}
                    strokeWidth="1"
                    opacity="0.4"
                  />
                )}
                <circle
                  cx={nodeX}
                  cy={nodeY}
                  r={nodeR}
                  fill={isH ? tierColor : "var(--card)"}
                  stroke={tierColor}
                  strokeWidth="1.5"
                  strokeDasharray={tier.variant === "dashed" ? "3 3" : undefined}
                />
                <NodeGlyph
                  visual={visual}
                  cx={nodeX}
                  cy={nodeY}
                  fill={isH ? "var(--bg)" : "var(--muted)"}
                  radius={nodeR}
                />
              </g>
            );
          })}
        </svg>
      </div>

      <TierTooltip
        tiers={tiers}
        active={effective}
        palette={palette}
        anchorRefs={anchorRefs}
      />

      <SwipeScroll className="w-full pt-6 border-t border-dashed border-[var(--border-dashed)]">
        <Legend
          items={legendItems}
          hoveredIndex={hovered}
          onHoverChange={setHovered}
          selectedIndex={selected}
          onSelect={select}
          onLabelRef={setLabelRef}
          className="flex flex-row gap-1"
        >
          <LegendItem className="flex shrink-0 flex-col items-start gap-1 px-3 py-2">
            <div className="flex items-baseline gap-2">
              <LegendIndex />
              <LegendLabel className="text-sm" />
            </div>
            <LegendValue className="text-xs text-legend-muted-foreground tabular-nums" />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </>
  );
}

function SegmentedPyramidChart({
  tiers,
  inverted,
  grayscale,
  accent,
}: {
  tiers: PyramidTier[];
  inverted: boolean;
  grayscale?: boolean;
  accent?: number;
}): JSX.Element {
  const { hovered, selected, effective, setHovered, select, setLabelRef } =
    useLegendHoverSelect();
  const hoverGesture = useHoverGesture();
  const palette = accentChartColors(grayscale, accent);

  const N = tiers.length;
  const svg = { w: 320, h: 320 };
  const maxWidth = 280;
  const height = maxWidth * (Math.sqrt(3) / 2);
  const top = (svg.h - height) / 2;
  const py = { cx: 160, top, height, maxWidth, tierHeight: height / N };
  const legendItems = useMemo(
    () => tiersToLegendItems(tiers, palette),
    [tiers, palette],
  );
  const { svgRef, height: dynamicH } = useSvgContentHeight(svg.h, [tiers]);
  const { anchorRefs, setAnchorRef } = useTierAnchors(tiers);

  return (
    <>
      <div className="shrink-0">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${svg.w} ${dynamicH}`}
          width={svg.w}
          height={dynamicH}
          className="max-w-full h-auto"
          style={{ overflow: "visible" }}
        >
          {tiers.map((tier, i) => {
            const g = tierGeom(i, N, py, inverted);
            const isH = effective === i;
            const isD = effective !== null && !isH;
            const tierColor = resolveTierColor(tier, i, palette);
            const visual = resolveVisual(tier, i);
            const isDashed = tier.variant === "dashed";
            const glyphColor = isDashed ? tierColor : "var(--bg)";

            const path = `
              M ${py.cx - g.topWidth / 2} ${g.yTop}
              L ${py.cx + g.topWidth / 2} ${g.yTop}
              L ${py.cx + g.bottomWidth / 2} ${g.yBottom}
              L ${py.cx - g.bottomWidth / 2} ${g.yBottom}
              Z
            `;

            return (
              <g
                key={tier.id}
                ref={setAnchorRef[tier.id]}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) setHovered(i);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) setHovered(null);
                }}
                onClick={() => select(i, { kind: "segment", id: tier.id })}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={isD ? 0.35 : 1}
              >
                <path
                  d={path}
                  fill={tierColor}
                  fillOpacity={isDashed ? (isH ? 0.14 : 0) : isH ? 1 : 0.85}
                  stroke={isDashed ? tierColor : "none"}
                  strokeWidth={isDashed ? 1.25 : undefined}
                  strokeDasharray={isDashed ? "5 4" : undefined}
                  strokeLinejoin="round"
                />
                <NodeGlyph
                  visual={visual}
                  cx={py.cx}
                  cy={g.centerY}
                  fill={glyphColor}
                  radius={14}
                  ring={glyphColor}
                />
              </g>
            );
          })}
        </svg>
      </div>

      <TierTooltip
        tiers={tiers}
        active={effective}
        palette={palette}
        anchorRefs={anchorRefs}
      />

      <SwipeScroll className="w-full pt-6 border-t border-dashed border-[var(--border-dashed)]">
        <Legend
          items={legendItems}
          hoveredIndex={hovered}
          onHoverChange={setHovered}
          selectedIndex={selected}
          onSelect={select}
          onLabelRef={setLabelRef}
          className="flex flex-row gap-1"
        >
          <LegendItem className="flex shrink-0 flex-col items-start gap-1 px-3 py-2">
            <div className="flex items-baseline gap-2">
              <LegendIndex />
              <LegendLabel className="text-sm" />
            </div>
            <LegendValue className="text-xs text-legend-muted-foreground tabular-nums" />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </>
  );
}

export default function ModulePyramid({
  data,
  variant = "nodes",
  inverted = false,
  grayscale,
  accent,
  className,
}: {
  data: ModulePyramidData;
  variant?: "nodes" | "segmented";
  inverted?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const tiers = data.tiers as PyramidTier[];

  return (
    <div
      className={cn("flex w-full justify-center", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="flex flex-col items-center gap-4 w-full max-w-[520px]">
        {variant === "segmented" ? (
          <SegmentedPyramidChart tiers={tiers} inverted={inverted} grayscale={grayscale} accent={accent} />
        ) : (
          <NodePyramidChart tiers={tiers} inverted={inverted} grayscale={grayscale} accent={accent} />
        )}
      </div>
    </div>
  );
}
